import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { useCalendarColors } from "@/components/calendars/calendarColors.ts";
import {
  getCalendarProviderIcon,
  getCalendarProviderLabel,
} from "@/components/calendars/calendarProviderUtils.tsx";
import { useBusyCalendars } from "@/components/calendars/useBusyCalendars.ts";
import { OmniPickerRow } from "@/components/artifacts/omni/OmniPickerRow.tsx";
import { useSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";

/**
 * The busy-calendar chooser, sized for the omnibox card.
 *
 * Same question and the same `useBusyCalendars` state as the canvas card, in a
 * denser row: checkbox, colour swatch, and name (`OmniPickerRow`). The
 * canvas has a screen height to spend and can afford the settings layout's
 * subtitles and per-account cards; here the card floats over the thread above a
 * "Done" row and a follow-up box, so the list is capped and scrolls rather than
 * pushing the composer off the top of the viewport.
 *
 * Picks save as they are made — the same autosave every other surface uses — so
 * "Done" ends the step rather than committing anything, and a user who dismisses
 * the card keeps whatever they had already chosen.
 */
export const CalendarsOmniPickerBody = () => {
  const { selectedCalendarIds } = useSurfacePreferences("calendars");
  const { groups, isLoading, hasMultipleConnectors, isPrimary, isSelected, toggle } =
    useBusyCalendars(selectedCalendarIds);
  // Read through the registry rather than off the calendar record, so a colour
  // the user changes elsewhere repaints these swatches too.
  const { getCalendarHex } = useCalendarColors();

  // Skeleton rows rather than a spinner: they hold the height the list is about
  // to take, so the card doesn't grow under the cursor as the calendars land.
  // (A spinner here would also drag the lottie player into every biscuit's
  // import graph — this body is reached from the shared card chrome.)
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-3">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3.5 w-44" />
        <Skeleton className="h-3.5 w-36" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      {/* Whole rows, never a sliced one: the 29px heading plus three 36px rows on
          a phone, where this card shares the screen with the keyboard, and the
          same heading plus five on a desktop. */}
      <OmniScrollArea className="max-h-[137px] md:max-h-[209px]">
        {groups.map((group) => (
          <div key={group.connectorId}>
            {/* Sticky, and in the scroll flow: that is what makes an account's
                band hold the top while its own rows pass under it and then get
                pushed off by the next account's. A band outside the scroller
                cannot do that — it never moves, so the arriving one stacks
                beneath it. Solid rather than tinted, because a translucent band
                shows every row passing behind it. */}
            <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-muted px-3 py-1.5 text-xs text-muted-foreground">
              {getCalendarProviderIcon(group.provider, "size-3.5")}
              <span className="truncate">
                {hasMultipleConnectors ? group.label : getCalendarProviderLabel(group.provider)}
              </span>
            </div>
            {group.calendars.map((calendar) => (
              <OmniPickerRow
                key={`${calendar.accountId}/${calendar.calendarId}`}
                checked={isSelected(calendar.calendarId)}
                onToggle={() => void toggle(calendar.calendarId)}
                // A primary calendar is always busy and can't be turned off:
                // locked shows its check at full contrast without a control.
                locked={isPrimary(calendar.calendarId)}
                label={calendar.summary || calendar.calendarId}
                // The calendar's color lives on the checkbox itself; a
                // separate color dot would say the same thing twice.
                checkboxColor={getCalendarHex(calendar.calendarId)}
                // A calendar name can repeat across accounts; the group's
                // account label keeps the accessible names apart.
                ariaContext={
                  hasMultipleConnectors ? group.label : getCalendarProviderLabel(group.provider)
                }
                qualifier={isPrimary(calendar.calendarId) ? "Default" : undefined}
                ariaState={isPrimary(calendar.calendarId) ? "Default" : undefined}
                statsigName="omni-picker-calendar-toggle"
              />
            ))}
          </div>
        ))}
      </OmniScrollArea>
    </div>
  );
};
